import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import TenantBrand from "@/components/TenantBrand";
import RouletteWheel from "@/components/RouletteWheel";
import { parseDelivered, profilePin } from "@/lib/deliveredFormat";
import { placeholderImage } from "@/lib/placeholderImage";
import { toast } from "sonner";
import { motion, useReducedMotion } from "framer-motion";
import {
  Plus, Minus, Trash2, X, LogOut, LayoutDashboard, Wallet, FolderClock,
  Search, FileText, Send, Bell, User, AlertTriangle, ShoppingCart, ShoppingBag, Calendar, CheckCircle, XCircle,
  Monitor, KeyRound, Copy, Clock, DollarSign, Lock, Info, Trophy, Medal, Flame, Ticket, Check, Globe, Mail, Gift, Users,
} from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Espejo de _price_for_qty en el backend: aplica los paquetes de precio fijo
// (bulk_prices) de mayor a menor cantidad y cobra el resto al precio normal.
// Solo para que el carrito muestre el mismo total que va a cobrar /checkout
// -- el precio real siempre se recalcula en el servidor.
const priceForQty = (unitPrice, bulkPrices, qty) => {
  const tiers = (bulkPrices || []).filter((t) => t.qty > 0).slice().sort((a, b) => b.qty - a.qty);
  let remaining = qty;
  let total = 0;
  for (const t of tiers) {
    const n = Math.floor(remaining / t.qty);
    if (n > 0) { total += n * t.price; remaining -= n * t.qty; }
  }
  total += remaining * unitPrice;
  return total;
};
const NOTIF_STYLES = {
  venta: { Icon: ShoppingCart, cls: "bg-emerald-500 text-white" },
  recarga: { Icon: Wallet, cls: "bg-purple-500 text-white" },
  reporte: { Icon: CheckCircle, cls: "bg-cyan-500 text-white" },
  alerta: { Icon: AlertTriangle, cls: "bg-amber-500 text-white" },
  info: { Icon: Info, cls: "bg-blue-500 text-white" },
};
const notifStyle = (type) => NOTIF_STYLES[type] || NOTIF_STYLES.info;

const TelegramLogo = ({ size = 17 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>
);

const BinanceLogo = ({ size = 17 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.624 13.9202l2.7175 2.7154-7.353 7.353-7.353-7.352 2.7175-2.7154 4.6355 4.6595 4.6356-4.6595zm4.6595-4.6595L24 12l-2.7154 2.7175L18.567 12l2.7165-2.7395zm-9.295 0L14.7058 12l-2.7146 2.7175L9.2778 12l2.7165-2.7395zm-9.295 0L5.4108 12 2.6934 14.7175 0 12l2.716-2.7395zM11.9885.0006l7.353 7.353-2.7175 2.7155-4.6355-4.6595-4.6356 4.6595L4.6355 7.353l7.353-7.3525z" />
  </svg>
);

export default function Store() {
  const { user, logout, refresh } = useAuth();
  const { tenant } = useTenant();
  const WHATSAPP = tenant.whatsapp_number ? `https://wa.me/${tenant.whatsapp_number}` : "";
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [qtys, setQtys] = useState({});
  const [q, setQ] = useState("");
  const [categoria, setCategoria] = useState("Todos");
  const [modal, setModal] = useState(null); // cart | history | recharge | terms | report | profile | notifs
  const [purchases, setPurchases] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [terms, setTerms] = useState(null);
  const [lastPurchase, setLastPurchase] = useState(null);
  const [lastPrizeCredit, setLastPrizeCredit] = useState(null);
  const [canvaSent, setCanvaSent] = useState({});
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState(null);
  const [referrals, setReferrals] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [rechargeTab, setRechargeTab] = useState("bank");
  const [bankMonto, setBankMonto] = useState("");
  const [bankNombre, setBankNombre] = useState("");
  const [binanceOrderId, setBinanceOrderId] = useState("");
  const [rechargeVerifying, setRechargeVerifying] = useState(false);
  const [bonusTiers, setBonusTiers] = useState([]);
  const [couponCode, setCouponCode] = useState("");
  const [couponSending, setCouponSending] = useState(false);
  const [linkEmailInput, setLinkEmailInput] = useState("");
  const [linkEmailCode, setLinkEmailCode] = useState("");
  const [linkEmailSent, setLinkEmailSent] = useState(false);
  const [linkEmailBusy, setLinkEmailBusy] = useState(false);
  const reduced = useReducedMotion();

  const load = async () => {
    const { data } = await api.get("/products");
    setProducts(data);
    setQtys(Object.fromEntries(data.map((p) => [p.id, 1])));
    setLoading(false);
  };
  const loadNotifs = async () => { const { data } = await api.get("/my/notifications"); setNotifs(data); };
  useEffect(() => { load(); loadNotifs(); }, []);
  useEffect(() => {
    if (!modal) return;
    const onKey = (e) => { if (e.key === "Escape") setModal(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modal]);
  const openLeaderboard = async () => { setModal("leaderboard"); const { data } = await api.get("/leaderboard"); setLeaderboard(data); };
  const openReferrals = async () => { setModal("referrals"); const { data } = await api.get("/my/referrals"); setReferrals(data); };
  const referralLink = referrals ? `${window.location.origin}/login?mode=register&ref=${referrals.code}` : "";

  const disponibles = products.filter((p) => p.stock > 0).length;
  const sinStock = products.filter((p) => p.stock === 0).length;
  // Orden: 1) con stock y con foto, 2) con stock sin foto (al final de lo
  // disponible, pero antes que lo agotado), 3) sin stock (siempre al final).
  const listTier = (p) => {
    if (p.stock === 0) return 2;
    return p.image ? 0 : 1;
  };
  const categorias = ["Todos", ...new Set(products.map((p) => p.category).filter(Boolean))];
  const filtered = products
    .filter((p) => p.name.toLowerCase().includes(q.toLowerCase()))
    .filter((p) => categoria === "Todos" || p.category === categoria)
    .slice()
    .sort((a, b) => listTier(a) - listTier(b));
  const unread = notifs.filter((n) => !n.read).length;

  const addToCart = (p) => {
    const qty = qtys[p.id] || 1;
    setCart((c) => {
      const ex = c.find((i) => i.id === p.id);
      if (ex) return c.map((i) => i.id === p.id ? { ...i, quantity: Math.min(i.quantity + qty, p.stock) } : i);
      return [...c, { id: p.id, name: p.name, price: p.price_final ?? p.price, bulk_prices: p.bulk_prices || [], quantity: qty, stock: p.stock, image: p.image }];
    });
    toast.success(`${p.name} añadido al carrito`);
  };
  const setQty = (id, v) => setCart((c) => c.map((i) => i.id === id ? { ...i, quantity: Math.max(1, Math.min(v, i.stock)) } : i));
  const total = cart.reduce((s, i) => s + priceForQty(i.price, i.bulk_prices, i.quantity), 0);
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);

  const checkout = async () => {
    if (checkingOut || cart.length === 0) return;
    setCheckingOut(true);
    try {
      const { data } = await api.post("/checkout", { items: cart.map((i) => ({ product_id: i.id, quantity: i.quantity })) });
      toast.success(`Compra realizada: ${money(data.total)}`);
      setLastPurchase({ saleId: data.sale_id, items: data.items });
      setCanvaSent({});
      setCart([]); setModal(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
      await Promise.all([load(), refresh(), loadNotifs()]);
    } catch (ex) { toast.error(apiError(ex.response?.data?.detail)); }
    finally { setCheckingOut(false); }
  };
  const loadPurchases = async () => { const { data } = await api.get("/my/purchases"); setPurchases(data); };
  const openHistory = async () => { await loadPurchases(); setModal("history"); };
  const openRecharge = () => {
    setBankMonto(""); setBankNombre(""); setBinanceOrderId(""); setModal("recharge");
    if (bonusTiers.length === 0) api.get("/recharge-bonus-tiers").then(({ data }) => setBonusTiers(data)).catch(() => {});
  };
  const verifyBank = async () => {
    const monto = Number(bankMonto);
    if (!monto || monto <= 0) return toast.error("Ingresa el monto que transferiste");
    if (!bankNombre.trim()) return toast.error("Ingresa el nombre tal como aparece en tu banco");
    setRechargeVerifying(true);
    try {
      const { data } = await api.post("/recharge/bank", { monto, nombre_titular: bankNombre.trim() });
      if (data.credited) {
        toast.success("¡Pago verificado! Tus créditos ya se agregaron.");
        setModal(null); setBankMonto(""); setBankNombre("");
        await Promise.all([refresh(), loadNotifs()]);
      } else {
        toast.warning("Aún no encontramos tu pago. Si ya transferiste, dale unos minutos y vuelve a darle a verificar.");
      }
    } catch (ex) { toast.error(apiError(ex.response?.data?.detail)); }
    finally { setRechargeVerifying(false); }
  };
  const verifyBinance = async () => {
    if (!binanceOrderId.trim()) return toast.error("Ingresa el Order ID de Binance");
    setRechargeVerifying(true);
    try {
      const { data } = await api.post("/recharge/binance", { order_id: binanceOrderId.trim() });
      if (data.credited) {
        toast.success("¡Pago verificado! Tus créditos ya se agregaron.");
        setModal(null); setBinanceOrderId("");
        await Promise.all([refresh(), loadNotifs()]);
      } else {
        toast.warning("Aún no encontramos ese Order ID. Si ya lo enviaste, dale unos minutos y vuelve a darle a verificar.");
      }
    } catch (ex) { toast.error(apiError(ex.response?.data?.detail)); }
    finally { setRechargeVerifying(false); }
  };
  const requestLinkEmail = async () => {
    if (!linkEmailInput.trim()) return toast.error("Escribe un correo");
    setLinkEmailBusy(true);
    try {
      await api.post("/account/link-email/request", { email: linkEmailInput.trim() });
      toast.success("Te mandamos un código a ese correo");
      setLinkEmailSent(true);
    } catch (ex) { toast.error(apiError(ex.response?.data?.detail)); }
    finally { setLinkEmailBusy(false); }
  };
  const confirmLinkEmail = async () => {
    if (!linkEmailCode.trim()) return toast.error("Escribe el código");
    setLinkEmailBusy(true);
    try {
      await api.post("/account/link-email/confirm", { code: linkEmailCode.trim() });
      toast.success("¡Correo vinculado! Ya puedes recuperar tu contraseña con él.");
      setLinkEmailSent(false); setLinkEmailInput(""); setLinkEmailCode("");
      await refresh();
    } catch (ex) { toast.error(apiError(ex.response?.data?.detail)); }
    finally { setLinkEmailBusy(false); }
  };
  const redeemCoupon = async () => {
    if (!couponCode.trim()) return toast.error("Escribe un código");
    setCouponSending(true);
    try {
      const { data } = await api.post("/coupons/redeem", { code: couponCode.trim() });
      toast.success(`¡Cupón canjeado! Se agregaron ${money(data.monto)} a tu cuenta.`);
      setCouponCode(""); await refresh();
    } catch (ex) { toast.error(apiError(ex.response?.data?.detail)); }
    finally { setCouponSending(false); }
  };
  // Optimista y sin refetch a propósito: pedir la lista completa de nuevo
  // después de marcar como leída podía llegar antes de que el POST terminara
  // de guardarse, y el número de sin-leer volvía a aparecer como si nada.
  const markRead = (id) => { setNotifs((ns) => ns.map((n) => n.id === id ? { ...n, read: true } : n)); api.post(`/notifications/${id}/read`).catch(() => {}); };
  const markAllRead = () => { setNotifs((ns) => ns.map((n) => ({ ...n, read: true }))); api.post("/notifications/read-all").catch(() => {}); };
  const dismissNotif = (id) => { setNotifs((ns) => ns.filter((n) => n.id !== id)); api.post(`/notifications/${id}/read`).catch(() => {}); };
  const shortDate = (d) => new Date(d).toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit" });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navbar */}
      <header className="sticky top-0 z-40 bg-primary text-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-2.5 min-h-[70px] flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <TenantBrand tenant={tenant} />
          <div className="flex flex-wrap items-center justify-end gap-2">
            <div data-testid="credit-balance" className="flex items-center gap-2 bg-white text-slate-900 rounded-full pl-4 pr-1.5 py-1.5 shadow-sm">
              <Wallet size={17} className="text-emerald-500" />
              <span className="font-bold text-sm">{money(user?.credits)}</span>
              <button data-testid="recharge-btn" aria-label="Recargar créditos" onClick={openRecharge} className="w-7 h-7 grid place-items-center rounded-full bg-emerald-500 hover:bg-emerald-600 text-white transition-colors"><Plus size={15} /></button>
            </div>
            <button data-testid="leaderboard-btn" aria-label="Mejores compradores del mes" onClick={openLeaderboard} className="w-10 h-10 grid place-items-center rounded-full bg-white text-amber-500 shadow-sm hover:bg-slate-100 transition-colors"><Trophy size={18} /></button>
            <button data-testid="referrals-btn" aria-label="Referidos" onClick={openReferrals} className="w-10 h-10 grid place-items-center rounded-full bg-white text-emerald-600 shadow-sm hover:bg-slate-100 transition-colors"><Users size={18} /></button>
            <div className="relative">
              <button data-testid="notif-btn" aria-label="Notificaciones" onClick={() => setModal(modal === "notifs" ? null : "notifs")} className="relative w-10 h-10 grid place-items-center rounded-full bg-white text-primary shadow-sm hover:bg-slate-100 transition-colors">
                <Bell size={18} />
                {unread > 0 && <span aria-live="polite" className="absolute -top-1 -right-1 bg-slate-900 text-white text-[10px] font-bold w-5 h-5 grid place-items-center rounded-full">{unread}<span className="sr-only"> notificaciones sin leer</span></span>}
              </button>
              {modal === "notifs" && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setModal(null)} />
                  <div className="fixed left-4 right-4 top-[78px] sm:absolute sm:left-0 sm:right-auto sm:top-full sm:mt-2 sm:w-96 bg-slate-900 text-white rounded-xl shadow-md z-50 overflow-hidden animate-fade-up">
                    <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-700">
                      <div className="flex items-center gap-2">
                        <Bell size={16} className="text-amber-400" />
                        <h3 className="font-head font-bold text-white text-sm">Notificaciones</h3>
                      </div>
                      {unread > 0 && (
                        <button onClick={markAllRead} className="flex items-center gap-1 text-[11px] font-semibold text-slate-300 hover:text-white transition-colors">
                          <Check size={12} /> Marcar todas
                        </button>
                      )}
                    </div>
                    <div className="max-h-96 overflow-y-auto divide-y divide-slate-800">
                      {notifs.length === 0 ? (
                        <p className="text-slate-400 text-sm text-center py-8">Sin notificaciones.</p>
                      ) : notifs.map((n) => {
                        const { Icon, cls } = notifStyle(n.type);
                        return (
                          <div key={n.id} className={`px-4 py-3 flex gap-3 items-start hover:bg-slate-800/50 transition-colors ${!n.read ? "bg-slate-800/40" : ""}`}>
                            <div className={`w-7 h-7 rounded-full grid place-items-center shrink-0 mt-0.5 ${cls}`}><Icon size={14} /></div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <p className={`text-xs font-bold uppercase tracking-wide flex items-center gap-1.5 ${n.read ? "text-slate-400" : "text-white"}`}>
                                  {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />}
                                  {n.title}
                                </p>
                                <span className="text-[10px] text-slate-500 shrink-0">{shortDate(n.created_at)}</span>
                              </div>
                              <p className={`text-sm mt-0.5 ${n.read ? "text-slate-500" : "text-slate-300"}`}>{n.message}</p>
                            </div>
                            <div className="flex flex-col items-center gap-2 shrink-0">
                              {!n.read && <button onClick={() => markRead(n.id)} aria-label="Marcar como leída" className="text-slate-500 hover:text-emerald-400"><Check size={14} /></button>}
                              <button onClick={() => dismissNotif(n.id)} aria-label="Quitar notificación" className="text-slate-500 hover:text-white"><X size={14} /></button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
            {user?.role === "admin" && (
              <button data-testid="go-admin-btn" onClick={() => navigate("/admin")} className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors"><LayoutDashboard size={16} /><span className="hidden md:inline">Panel Admin</span></button>
            )}
            <button data-testid="profile-btn" onClick={() => setModal("profile")} className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors"><User size={16} /><span className="hidden sm:inline">Perfil</span></button>
            <button data-testid="logout-btn" onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors"><LogOut size={16} /><span className="hidden sm:inline">Salir</span></button>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-center font-bold text-slate-800 text-lg mb-6">Bienvenido a tu panel: <span className="text-primary">{user?.name}</span></h1>

        {lastPrizeCredit && (
          <div className="max-w-3xl mx-auto mb-6 bg-emerald-50 border border-emerald-200 rounded-2xl p-5 relative animate-fade-up">
            <button onClick={() => setLastPrizeCredit(null)} aria-label="Cerrar" className="absolute top-4 right-4 text-emerald-600 hover:text-emerald-800"><X size={18} /></button>
            <p className="flex items-center gap-2 text-emerald-700 font-bold mb-1"><Gift size={18} /> ¡Felicidades!</p>
            <p className="text-slate-700">Ganaste <b>{money(lastPrizeCredit.valor)}</b> de saldo en la ruleta — ya fue cargado a tu cuenta.</p>
          </div>
        )}

        {lastPurchase && (
          <div className="max-w-3xl mx-auto mb-6 bg-emerald-50 border border-emerald-200 rounded-2xl p-5 relative animate-fade-up">
            <button onClick={() => setLastPurchase(null)} aria-label="Cerrar" className="absolute top-4 right-4 text-emerald-600 hover:text-emerald-800"><X size={18} /></button>
            <p className="flex items-center gap-2 text-emerald-700 font-bold mb-4"><CheckCircle size={18} /> Compra realizada con éxito</p>
            <div className="space-y-4">
              {lastPurchase.items.filter((it) => it.delivery_mode === "canva_invite").map((it) => (
                <CanvaInviteBox key={it.product_id} saleId={lastPurchase.saleId} productId={it.product_id} productName={it.product_name}
                  total={it.quantity} sent={canvaSent[it.product_id] || 0}
                  onSent={() => setCanvaSent((s) => ({ ...s, [it.product_id]: (s[it.product_id] || 0) + 1 }))} />
              ))}
              {lastPurchase.items.filter((it) => it.delivery_mode !== "canva_invite").flatMap((it) => it.delivered.map((d, di) => {
                const p = parseDelivered(d);
                const usuario = p.texto || p.correo || d;
                const contrasena = p.contrasena || "";
                const rows = [{ label: "Usuario", value: usuario }, { label: "Contraseña", value: contrasena }];
                if (p.url) rows.push({ label: "URL", value: p.url });
                if (p.perfil) {
                  rows.push({ label: "Perfil", value: p.perfil });
                  const pin = profilePin(it.product_name, p.perfil);
                  if (pin) rows.push({ label: "PIN", value: pin });
                }
                if (p.dispositivo) rows.push({ label: "Dispositivo", value: p.dispositivo });
                return (
                  <div key={`${it.product_id}-${di}`}>
                    <p className="font-bold text-slate-900 mb-2">{it.product_name}</p>
                    {rows.map((row) => (
                      <div key={row.label} className="flex items-center gap-2 bg-white border border-emerald-200 rounded-lg px-4 py-2.5 mb-2 last:mb-0">
                        <span className="text-sm font-semibold text-slate-700 shrink-0">{row.label}:</span>
                        <span className="flex-1 text-sm text-slate-600 truncate">{row.value}</span>
                        <button onClick={() => copyText(row.value)} aria-label={`Copiar ${row.label.toLowerCase()}`} className="w-7 h-7 shrink-0 grid place-items-center bg-emerald-500 hover:bg-emerald-600 text-white rounded"><Copy size={13} /></button>
                      </div>
                    ))}
                  </div>
                );
              }))}
            </div>
            <button onClick={() => copyText(lastPurchase.items.filter((it) => it.delivery_mode !== "canva_invite").flatMap((it) => it.delivered.map((d) => formatDeliveredForCopy(it.product_name, d))).join("\n\n"))}
              className="mt-4 w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-md py-3 transition-colors">
              <Copy size={16} /> Copiar todo
            </button>
          </div>
        )}

        <div className="max-w-md mx-auto relative mb-5">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar servicios..."
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors" />
        </div>

        {categorias.length > 1 && (
          <div className="flex justify-center gap-2 mb-5 flex-wrap">
            {categorias.map((c) => (
              <button key={c} data-testid={`cat-${c}`} onClick={() => setCategoria(c)}
                className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors ${categoria === c ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary hover:text-primary"}`}>
                {c}
              </button>
            ))}
          </div>
        )}

        <div className="flex justify-center gap-3 mb-8 flex-wrap">
          <Pill icon={Calendar} text={`${products.length} servicios`} tone="slate" />
          <Pill icon={CheckCircle} text={`${disponibles} disponibles`} tone="green" />
          <Pill icon={XCircle} text={`${sinStock} sin stock`} tone="red" />
        </div>

        {loading ? (
          <div className="grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 overflow-hidden bg-white animate-pulse">
                <div className="h-28 sm:h-44 bg-slate-100" />
                <div className="p-2.5 sm:p-4 space-y-3">
                  <div className="h-4 bg-slate-100 rounded w-3/4" />
                  <div className="h-3 bg-slate-100 rounded w-1/2" />
                  <div className="h-8 bg-slate-100 rounded mt-4" />
                </div>
              </div>
            ))}
          </div>
        ) : (
        <div className="grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((p, i) => {
            const out = p.stock === 0;
            return (
              <motion.div key={p.id} data-testid={`product-${p.id}`}
                initial={reduced ? false : { opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: reduced ? 0 : 0.35, delay: reduced ? 0 : Math.min(i * 0.03, 0.3), ease: [0.16, 1, 0.3, 1] }}
                className={`rounded-2xl border overflow-hidden flex flex-col transition-transform duration-200 bg-white ${out ? "border-slate-200 opacity-60" : "border-slate-200 hover:-translate-y-1"}`}>
                <div className="relative h-28 sm:h-44 bg-slate-100">
                  <img src={p.image || placeholderImage(p.name, tenant.primary_color)} alt={p.name} loading="lazy"
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = placeholderImage(p.name, tenant.primary_color); }}
                    className={`w-full h-full object-cover ${out ? "grayscale opacity-50" : ""}`} />
                  {p.discount_pct > 0 && (
                    <span className="absolute top-2 left-2 bg-primary text-white text-[10px] sm:text-xs font-extrabold px-2 py-1 rounded-md shadow">-{p.discount_pct}%</span>
                  )}
                  <button onClick={() => { setTerms(p); setModal("terms"); }} className="absolute top-2 right-2 flex items-center gap-1 bg-white/90 backdrop-blur text-slate-700 text-[10px] sm:text-[11px] font-semibold px-1.5 sm:px-2 py-1 rounded-md shadow hover:bg-white transition-colors"><FileText size={11} /> <span className="hidden sm:inline">Terminos</span></button>
                  {out && (
                    <div className="absolute inset-0 grid place-items-center">
                      <span className="bg-white/95 text-primary text-[10px] sm:text-[11px] font-bold px-2 sm:px-3 py-1 sm:py-1.5 rounded-full flex items-center gap-1 shadow"><XCircle size={12} /> SIN STOCK</span>
                    </div>
                  )}
                </div>
                <div className="p-2.5 sm:p-4 flex flex-col flex-1">
                  <h3 className={`font-head font-bold leading-tight text-sm sm:text-base ${out ? "text-slate-500" : "text-slate-900"}`}>{p.name}</h3>
                  {p.tags.length > 0 && (
                    <div className="flex flex-wrap justify-center gap-1.5 mt-2">
                      {p.tags.map((t) => <span key={t} className="text-[10px] uppercase tracking-wide bg-amber-50 text-amber-700 border border-amber-100 font-semibold px-2 py-0.5 rounded">{t}</span>)}
                    </div>
                  )}
                  <div className="mt-auto pt-3 sm:pt-4">
                    <div className="flex items-end justify-between mb-2 sm:mb-3">
                      <div>
                        {p.discount_pct > 0 ? (
                          <div className="flex items-baseline gap-1.5 sm:gap-2 flex-wrap">
                            <span className={`font-head text-lg sm:text-2xl font-extrabold ${out ? "text-slate-300" : "text-primary"}`}>${Number(p.price_final).toFixed(2)}</span>
                            <span className="text-xs sm:text-sm text-slate-400 line-through">${Number(p.price).toFixed(2)}</span>
                          </div>
                        ) : (
                          <span className={`font-head text-lg sm:text-2xl font-extrabold ${out ? "text-slate-300" : "text-primary"}`}>${Number(p.price).toFixed(2)}</span>
                        )}
                      </div>
                      <span className="text-[10px] sm:text-xs flex items-center gap-1 text-slate-500"><ShoppingCart size={12} /> {p.stock}</span>
                    </div>
                    {(p.bulk_prices || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {p.bulk_prices.map((t) => (
                          <span key={t.qty} className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] text-amber-700 bg-amber-50 border border-amber-100 font-semibold px-2 py-0.5 rounded">
                            <Ticket size={11} className="text-amber-500" />
                            {t.qty}x <span className="font-extrabold">{money(t.price)}</span>
                          </span>
                        ))}
                      </div>
                    )}
                    {out ? (
                      <button disabled className="w-full bg-slate-100 text-slate-500 font-semibold rounded-md py-2 text-xs sm:text-sm cursor-not-allowed">Sin Stock</button>
                    ) : (
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <select data-testid={`qty-${p.id}`} value={qtys[p.id] || 1} onChange={(e) => setQtys((s) => ({ ...s, [p.id]: Number(e.target.value) }))}
                          className="border border-slate-200 rounded-md px-1.5 sm:px-2 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-red-500">
                          {Array.from({ length: Math.min(p.stock, Math.max(10, ...(p.bulk_prices || []).map((t) => t.qty), 0)) }, (_, k) => k + 1).map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                        <button data-testid={`add-${p.id}`} onClick={() => addToCart(p)} className="flex-1 flex items-center justify-center gap-1 sm:gap-1.5 bg-primary hover:bg-primary-hover text-white font-semibold rounded-md py-2 text-xs sm:text-sm transition-colors"><Plus size={14} /> Añadir</button>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
        )}
        {!loading && filtered.length === 0 && <p className="text-center text-slate-500 py-20">No se encontraron servicios.</p>}
      </div>

      {/* Floating action buttons */}
      <div className="fixed right-4 bottom-6 z-30 flex flex-col gap-2.5">
        <RouletteWheel onWon={(prize, compra) => {
          refresh(); loadPurchases(); loadNotifs();
          // Un premio de cuenta se muestra igual que una compra recién hecha
          // (banner verde con las credenciales) -- un premio de saldo no
          // tiene credenciales que mostrar, así que solo se refleja en el
          // saldo de arriba (ya cubierto por refresh()).
          if (compra) {
            setLastPurchase({ saleId: compra.sale_id, items: compra.items });
            window.scrollTo({ top: 0, behavior: "smooth" });
          } else if (prize?.tipo === "saldo") {
            setLastPrizeCredit(prize);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        }} />
        <FloatBtn testid="fab-report" label="Reportar un problema" onClick={() => navigate("/reportes")} className="bg-primary hover:bg-primary-hover"><AlertTriangle size={16} /></FloatBtn>
        <FloatBtn testid="fab-codigos" label="Pedir código de acceso" onClick={() => navigate("/codigos")} className="bg-primary hover:bg-primary-hover"><KeyRound size={16} /></FloatBtn>
        <FloatBtn testid="fab-history" label="Historial de compras" onClick={openHistory} className="bg-primary hover:bg-primary-hover"><FolderClock size={16} /></FloatBtn>
        <FloatBtn testid="fab-canva-mails" label="Mis invitaciones" onClick={() => setModal("canva")} className="bg-primary hover:bg-primary-hover"><Mail size={16} /></FloatBtn>
        {WHATSAPP && (
          <a href={WHATSAPP} target="_blank" rel="noreferrer" aria-label="Contactar por WhatsApp" data-testid="fab-whatsapp" className="w-11 h-11 grid place-items-center rounded-full shadow-md bg-white hover:bg-slate-100 text-[#25D366] transition-colors">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M17.6 6.3A7.9 7.9 0 0 0 12 4a7.9 7.9 0 0 0-6.8 11.9L4 20l4.2-1.1A7.9 7.9 0 0 0 12 20a7.9 7.9 0 0 0 5.6-13.7ZM12 18.5c-1.2 0-2.4-.3-3.4-.9l-.2-.1-2.5.7.7-2.4-.2-.3a6.6 6.6 0 1 1 5.6 3ZM15.6 13.8c-.2-.1-1.2-.6-1.3-.6-.2-.1-.3-.1-.4.1s-.5.6-.6.7-.2.1-.4 0a5.3 5.3 0 0 1-2.6-2.3c-.2-.3.2-.3.5-1 .1-.1 0-.3 0-.4l-.6-1.4c-.2-.4-.3-.3-.4-.3h-.4c-.1 0-.4.1-.6.3-.2.2-.8.8-.8 1.9s.8 2.2.9 2.4c.1.1 1.6 2.5 3.9 3.4.5.2 1 .4 1.3.5.5.2 1 .1 1.4.1.4-.1 1.2-.5 1.3-1s.2-.9.1-1c0-.1-.1-.2-.3-.3Z"/></svg>
          </a>
        )}
        <FloatBtn testid="fab-cart" label="Ver carrito" onClick={() => setModal("cart")} className="bg-primary hover:bg-primary-hover relative">
          <ShoppingCart size={16} />
          {cartCount > 0 && <span className="absolute -top-1 -right-1 bg-slate-900 text-white text-[10px] font-bold w-5 h-5 grid place-items-center rounded-full">{cartCount}</span>}
        </FloatBtn>
      </div>

      {/* Cart modal */}
      {modal === "cart" && (
        <div role="dialog" aria-modal="true" aria-label="Mi Carrito" className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full max-h-[85vh] flex flex-col overflow-hidden animate-fade-up shadow-md">
            <div className="flex items-center justify-between px-6 py-4 bg-primary text-white shrink-0">
              <div className="flex items-center gap-2"><ShoppingCart size={18} /><h3 className="font-head text-lg font-extrabold">Mi Carrito</h3></div>
              <button onClick={() => setModal(null)} aria-label="Cerrar" className="text-white/80 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-6 overflow-y-auto">
              {cart.length === 0 ? (
                <div className="text-center py-10">
                  <ShoppingBag size={40} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Tu carrito está vacío</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {cart.map((i) => {
                    const normalTotal = i.price * i.quantity;
                    const finalTotal = priceForQty(i.price, i.bulk_prices, i.quantity);
                    const hasBulkDeal = finalTotal < normalTotal - 0.005;
                    return (
                    <div key={i.id} data-testid={`cart-item-${i.id}`} className="flex items-center justify-between gap-3 bg-slate-50 rounded-lg p-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-sm text-slate-900 truncate">{i.name}</p>
                        <p className="text-xs text-slate-500">{money(i.price)} c/u</p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="flex items-center gap-2">
                          <button onClick={() => setQty(i.id, i.quantity - 1)} aria-label="Disminuir cantidad" className="w-6 h-6 grid place-items-center border border-slate-200 rounded bg-white text-slate-600 hover:bg-slate-100 hover:border-slate-300 transition-colors"><Minus size={12} /></button>
                          <span className="text-sm w-4 text-center" aria-live="polite">{i.quantity}</span>
                          <button onClick={() => setQty(i.id, i.quantity + 1)} aria-label="Aumentar cantidad" className="w-6 h-6 grid place-items-center border border-slate-200 rounded bg-white text-slate-600 hover:bg-slate-100 hover:border-slate-300 transition-colors"><Plus size={12} /></button>
                        </div>
                        {hasBulkDeal ? (
                          <div className="flex flex-col items-end leading-tight">
                            <span className="text-[10px] text-slate-400 line-through">{money(normalTotal)}</span>
                            <span className="text-primary font-bold text-sm">{money(finalTotal)}</span>
                          </div>
                        ) : (
                          <span className="text-primary font-bold text-sm">{money(finalTotal)}</span>
                        )}
                        <button onClick={() => setCart((c) => c.filter((x) => x.id !== i.id))} aria-label="Quitar del carrito" className="text-slate-400 hover:text-red-600"><Trash2 size={16} /></button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="border-t border-slate-100 px-6 py-5 shrink-0">
              <div className="flex justify-between items-center mb-4"><span className="text-xs font-bold uppercase tracking-wide text-slate-500">Total</span><span data-testid="cart-total" className="font-head text-2xl font-extrabold text-primary">{money(total)}</span></div>
              <button data-testid="checkout-btn" disabled={cart.length === 0 || checkingOut} onClick={checkout}
                className={`w-full flex items-center justify-center gap-2 font-semibold rounded-md py-3.5 transition-colors ${cart.length === 0 || checkingOut ? "bg-slate-200 text-slate-500 cursor-not-allowed" : "bg-primary hover:bg-primary-hover text-white"}`}>
                <Lock size={16} /> {checkingOut ? "Procesando..." : "Finalizar Compra"}
              </button>
              {total > (user?.credits || 0) && cart.length > 0 && (
                <button onClick={() => { setModal(null); openRecharge(); }} className="w-full text-xs text-red-600 text-center mt-2 underline underline-offset-2 hover:text-red-700">
                  Créditos insuficientes. Toca aquí para recargar.
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* History */}
      {modal === "history" && <HistoryModal purchases={purchases} onClose={() => setModal(null)} onReload={loadPurchases} />}

      {/* Mis invitaciones (Canva, Duolingo, Gemini, etc. -- cualquier producto por invitación) */}
      {modal === "canva" && <CanvaMailsModal onClose={() => setModal(null)} />}

      {/* Terms modal */}
      {modal === "terms" && terms && (
        <Center onClose={() => setModal(null)}>
          <h3 className="font-head text-xl font-extrabold text-slate-900">Términos del Servicio</h3>
          <p className="text-primary font-semibold mt-1">{terms.name}</p>
          <ul className="text-slate-600 mt-3 leading-relaxed list-disc list-inside space-y-1.5">
            {(terms.terms || "Sin términos especificados.").split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((line, i) => <li key={i}>{line}</li>)}
          </ul>
          <button onClick={() => setModal(null)} className="mt-6 w-full bg-primary hover:bg-primary-hover text-white font-semibold rounded-md py-2.5 transition-colors">Entendido</button>
        </Center>
      )}

      {/* Recharge */}
      {modal === "recharge" && (
        <Center onClose={() => setModal(null)}>
          <h3 className="font-head text-xl font-extrabold text-slate-900 flex items-center gap-2"><Wallet size={20} className="text-primary" /> Recarga tus créditos en Automático</h3>

          <div className="flex gap-2 mt-4">
            <button onClick={() => setRechargeTab("bank")}
              className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-semibold border transition-colors ${rechargeTab === "bank" ? "bg-primary text-white border-primary" : "border-slate-200 text-slate-600 hover:border-primary hover:text-primary"}`}>
              <Send size={14} /> Transferencia MXN
            </button>
            {tenant.binance_pay_enabled && (
              <button onClick={() => setRechargeTab("binance")}
                className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-semibold border transition-colors ${rechargeTab === "binance" ? "bg-primary text-white border-primary" : "border-slate-200 text-slate-600 hover:border-primary hover:text-primary"}`}>
                <BinanceLogo size={14} /> Binance (USDT)
              </button>
            )}
          </div>

          {bonusTiers.length > 0 && (
            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3.5">
              <div className="flex items-center gap-2">
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-emerald-600 text-white"><Gift size={14} /></span>
                <p className="text-sm font-bold text-emerald-900">Recarga Boost — créditos extra gratis</p>
              </div>
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {bonusTiers.map((t) => (
                  <span key={t.piso} className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-white px-2.5 py-1 text-xs text-emerald-800">
                    <b className="font-bold">+{Math.round(t.pct * 100)}%</b> desde ${t.piso}
                  </span>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-emerald-700">El bono se acredita solo, junto con tu saldo.</p>
            </div>
          )}

          {rechargeTab === "bank" ? (
            <div className="mt-4">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm space-y-1.5">
                <p><b>Banco:</b> {tenant.bank_name || "—"}</p>
                <p className="flex items-center gap-1.5"><b>CLABE:</b> {tenant.bank_clabe || "—"}
                  {tenant.bank_clabe && <button onClick={() => copyText(tenant.bank_clabe)} aria-label="Copiar CLABE" className="text-primary hover:text-primary-hover"><Copy size={13} /></button>}
                </p>
                <p><b>Titular:</b> {tenant.bank_account_holder || "—"}</p>
              </div>
              <p className="text-xs text-slate-500 mt-2">Transfiere el monto exacto y verifica aquí abajo.</p>

              <label className="block mt-3">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1 block">¿Cuántos pesos transferiste? (MXN)</span>
                <input type="number" min="1" value={bankMonto} onChange={(e) => setBankMonto(e.target.value)} placeholder="Ej: 150"
                  className="w-full border border-slate-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors" />
              </label>
              <label className="block mt-3">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1 block">Nombre del titular que envió el pago</span>
                <input value={bankNombre} onChange={(e) => setBankNombre(e.target.value)} placeholder="Tal como aparece en tu banco"
                  className="w-full border border-slate-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors" />
              </label>
              <p className="text-[11px] text-amber-600 mt-1.5">⚠ Si el nombre no coincide con el pago, no podremos verificarlo.</p>

              <button disabled={rechargeVerifying} onClick={verifyBank}
                className="mt-4 w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover disabled:opacity-60 text-white font-bold rounded-md py-3 transition-colors">
                <CheckCircle size={17} /> {rechargeVerifying ? "Verificando..." : "Pago realizado, verificar"}
              </button>
            </div>
          ) : (
            <div className="mt-4">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm space-y-1.5">
                <p><b>Binance Pay ID:</b> {tenant.binance_pay_id || "—"}
                  {tenant.binance_pay_id && <button onClick={() => copyText(tenant.binance_pay_id)} aria-label="Copiar ID" className="text-primary hover:text-primary-hover ml-1.5"><Copy size={13} /></button>}
                </p>
                {tenant.binance_pay_name && <p><b>Nombre:</b> {tenant.binance_pay_name}</p>}
                <p className="text-xs text-slate-500 pt-1">Envía solo USDT o USDC, copia el Order ID de tu comprobante y pégalo abajo.</p>
              </div>
              <label className="block mt-3">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1 block">Order ID de Binance</span>
                <input value={binanceOrderId} onChange={(e) => setBinanceOrderId(e.target.value)} placeholder="Ej: 2233445566778899"
                  className="w-full border border-slate-200 rounded-md px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors" />
              </label>
              <button disabled={rechargeVerifying} onClick={verifyBinance}
                className="mt-4 w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover disabled:opacity-60 text-white font-bold rounded-md py-3 transition-colors">
                <CheckCircle size={17} /> {rechargeVerifying ? "Verificando..." : "Verificar pago"}
              </button>
            </div>
          )}

          {(WHATSAPP || tenant.telegram_bot_username) && (
            <p className="text-center text-xs text-slate-400 mt-4 pt-4 border-t border-slate-100">
              ¿Prefieres hacerlo por chat?{" "}
              {WHATSAPP && <a href={WHATSAPP} target="_blank" rel="noreferrer" className="text-primary font-semibold hover:underline">WhatsApp</a>}
              {WHATSAPP && tenant.telegram_bot_username && " · "}
              {tenant.telegram_bot_username && <a href={`https://t.me/${tenant.telegram_bot_username}`} target="_blank" rel="noreferrer" className="text-primary font-semibold hover:underline">Telegram</a>}
            </p>
          )}
        </Center>
      )}

      {/* Profile */}
      {modal === "profile" && (
        <Center onClose={() => setModal(null)}>
          <h3 className="font-head text-xl font-extrabold text-slate-900 flex items-center gap-2"><User size={20} className="text-primary" /> Mi Perfil</h3>
          <div className="mt-4 space-y-2 text-sm">
            <Row label="Usuario" value={user?.name} />
            <Row label="Correo" value={user?.email} />
            <Row label="Créditos" value={money(user?.credits)} />
            <Row label="Rol" value={user?.role} />
            {user?.telegram && <Row label="Telegram" value={user.telegram} />}
          </div>
          {tenant.email_enabled && (
          <div className="mt-5 pt-5 border-t border-slate-100">
            <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 mb-1"><Mail size={15} className="text-primary" /> Correo de recuperación</p>
            {user?.real_email ? (
              <p className="text-xs text-slate-500">Vinculado: <span className="font-semibold text-slate-700">{user.real_email}</span></p>
            ) : linkEmailSent ? (
              <>
                <p className="text-xs text-slate-500 mb-2">Escribe el código que te llegó a {linkEmailInput}.</p>
                <div className="flex items-center gap-2">
                  <input value={linkEmailCode} onChange={(e) => setLinkEmailCode(e.target.value)} placeholder="123456" inputMode="numeric" maxLength={6}
                    className="flex-1 border border-slate-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors" />
                  <button onClick={confirmLinkEmail} disabled={linkEmailBusy}
                    className="shrink-0 bg-primary hover:bg-primary-hover disabled:opacity-60 text-white font-semibold rounded-lg px-4 py-2.5 text-sm transition-colors">
                    {linkEmailBusy ? "..." : "Confirmar"}
                  </button>
                </div>
                <button onClick={() => setLinkEmailSent(false)} className="text-[11px] text-slate-400 hover:text-primary mt-1">Usar otro correo</button>
              </>
            ) : (
              <>
                <p className="text-xs text-slate-500 mb-2">Necesitas esto para poder recuperar tu contraseña si la olvidas. Es un paso único.</p>
                <div className="flex items-center gap-2">
                  <input value={linkEmailInput} onChange={(e) => setLinkEmailInput(e.target.value)} placeholder="tu@correo.com" type="email"
                    className="flex-1 border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors" />
                  <button onClick={requestLinkEmail} disabled={linkEmailBusy}
                    className="shrink-0 bg-primary hover:bg-primary-hover disabled:opacity-60 text-white font-semibold rounded-lg px-4 py-2.5 text-sm transition-colors">
                    {linkEmailBusy ? "..." : "Enviar código"}
                  </button>
                </div>
              </>
            )}
          </div>
          )}
          <div className="mt-5 pt-5 border-t border-slate-100">
            <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 mb-1"><Ticket size={15} className="text-primary" /> Canjear cupón</p>
            <p className="text-xs text-slate-500 mb-2">¿Tienes un código? Canjéalo aquí y te agregamos los créditos al instante.</p>
            <div className="flex items-center gap-2">
              <input value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} placeholder="CODIGO"
                className="flex-1 border border-slate-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors" />
              <button onClick={redeemCoupon} disabled={couponSending}
                className="shrink-0 bg-primary hover:bg-primary-hover disabled:opacity-60 text-white font-semibold rounded-lg px-4 py-2.5 text-sm transition-colors">
                {couponSending ? "..." : "Canjear"}
              </button>
            </div>
          </div>
          <button onClick={() => setModal(null)} className="mt-6 w-full bg-primary hover:bg-primary-hover text-white font-semibold rounded-md py-2.5 transition-colors">Cerrar</button>
        </Center>
      )}

      {/* Leaderboard */}
      {modal === "leaderboard" && (
        <Center onClose={() => setModal(null)}>
          <h3 className="font-head text-xl font-extrabold text-slate-900 flex items-center gap-2"><Trophy size={20} className="text-amber-500" /> Mejores Compradores del Mes</h3>
          {!leaderboard ? (
            <p className="text-slate-400 text-center py-10">Cargando...</p>
          ) : (
            <>
              <p className="text-slate-500 text-sm mt-1">{capitalizeFirst(new Date(leaderboard.month).toLocaleDateString("es-MX", { month: "long", year: "numeric" }))}</p>
              {leaderboard.last_month_winner && (
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
                  <span className="text-2xl shrink-0">🏆</span>
                  <p className="text-sm text-amber-900">
                    <b>Comprador del mes pasado:</b> {leaderboard.last_month_winner.name} con {money(leaderboard.last_month_winner.total)}
                  </p>
                </div>
              )}
              {leaderboard.top.length === 0 ? (
                <p className="text-slate-400 text-center py-10">Todavía no hay compras este mes. ¡Sé el primero del podio!</p>
              ) : (
                <div className="mt-4 space-y-2">
                  {leaderboard.top.map((r) => {
                    const isMe = r.user_id === user?.id;
                    const rankCls =
                      r.rank === 1 ? "bg-amber-500 text-white"
                      : r.rank === 2 ? "bg-slate-400 text-white"
                      : r.rank === 3 ? "bg-amber-700 text-white"
                      : "bg-slate-200 text-slate-700";
                    return (
                      <div key={r.user_id} className={`flex items-center justify-between gap-3 rounded-xl px-4 py-3 ${isMe ? "bg-primary/10 ring-1 ring-primary" : "bg-slate-50"}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <span className={`w-7 h-7 shrink-0 rounded-full grid place-items-center text-xs font-extrabold ${rankCls}`}>{r.rank}</span>
                          <span className={`font-semibold truncate ${isMe ? "text-primary" : "text-slate-800"}`}>{r.name}{isMe ? " (tú)" : ""}</span>
                        </div>
                        <span className="font-bold text-slate-900 shrink-0">{money(r.total)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              {leaderboard.my_rank && leaderboard.my_rank > leaderboard.top.length && (
                <div className="mt-4 bg-primary/5 border border-primary/20 rounded-xl p-4 flex items-center gap-3">
                  <Flame size={20} className="text-primary shrink-0" />
                  <p className="text-sm text-slate-700"><b>Vas en el puesto #{leaderboard.my_rank}</b> con {money(leaderboard.my_total)} este mes. ¡Sigue comprando para subir al podio!</p>
                </div>
              )}
            </>
          )}
          <button onClick={() => setModal(null)} className="mt-6 w-full bg-primary hover:bg-primary-hover text-white font-semibold rounded-md py-2.5 transition-colors">Cerrar</button>
        </Center>
      )}

      {/* Referidos */}
      {modal === "referrals" && (
        <Center onClose={() => setModal(null)}>
          <h3 className="font-head text-xl font-extrabold text-slate-900 flex items-center gap-2"><Users size={20} className="text-emerald-600" /> Referidos</h3>
          {!referrals ? (
            <p className="text-slate-400 text-center py-10">Cargando...</p>
          ) : (
            <>
              <p className="text-sm text-slate-500 mt-1">
                Gana {Math.round(referrals.commission_pct * 100)}% de cada recarga que hagan las personas que invites, de por vida.
              </p>
              <div className="mt-4 bg-slate-50 border border-slate-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-slate-500 mb-1.5">Tu link de invitación</p>
                <div className="flex items-center gap-2">
                  <input readOnly value={referralLink} onClick={(e) => e.target.select()}
                    className="flex-1 min-w-0 bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-xs font-mono text-slate-700" />
                  <button onClick={() => copyText(referralLink)} aria-label="Copiar link"
                    className="shrink-0 bg-primary hover:bg-primary-hover text-white rounded-lg px-3 py-2.5 transition-colors"><Copy size={15} /></button>
                </div>
                <p className="text-[11px] text-slate-400 mt-1.5">O comparte solo tu código: <span className="font-mono font-semibold text-slate-600">{referrals.code}</span></p>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4">
                <div className="bg-emerald-50 rounded-xl p-3 text-center">
                  <p className="text-[11px] text-emerald-700 font-semibold">Ganado hoy</p>
                  <p className="font-bold text-emerald-800">{money(referrals.ganado_hoy)}</p>
                </div>
                <div className="bg-primary/5 rounded-xl p-3 text-center">
                  <p className="text-[11px] text-primary font-semibold">Total ganado</p>
                  <p className="font-bold text-slate-900">{money(referrals.total_ganado)}</p>
                </div>
                <div className="bg-slate-100 rounded-xl p-3 text-center">
                  <p className="text-[11px] text-slate-500 font-semibold">Referidos</p>
                  <p className="font-bold text-slate-900">{referrals.referidos.length}</p>
                </div>
              </div>
              <p className="text-[11px] text-slate-400 mt-2 text-center">Tope de {money(referrals.daily_cap)}/día, sumando todos tus referidos.</p>

              <p className="text-xs font-semibold text-slate-500 mt-5 mb-1.5">Cuánto te ha dado cada referido</p>
              {referrals.comisiones.length === 0 ? (
                <p className="text-slate-400 text-sm text-center py-6">Todavía no tienes comisiones. ¡Comparte tu link!</p>
              ) : (
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {Object.values(referrals.comisiones.reduce((acc, c) => {
                    const g = acc[c.referred_name] || { name: c.referred_name, total: 0, recargado: 0, count: 0, ultima: c.created_at };
                    g.total += c.comision; g.recargado += c.monto_base; g.count += 1;
                    if (c.created_at > g.ultima) g.ultima = c.created_at;
                    acc[c.referred_name] = g;
                    return acc;
                  }, {})).sort((a, b) => b.total - a.total).map((r) => (
                    <div key={r.name} className="flex items-center justify-between gap-2 bg-slate-50 rounded-lg px-3 py-2 text-sm">
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-800 truncate">{r.name}</p>
                        <p className="text-[11px] text-slate-400">
                          Recargó {money(r.recargado)} en total ({r.count} recarga{r.count === 1 ? "" : "s"}) · última {shortDate(r.ultima)}
                        </p>
                      </div>
                      <span className="font-bold text-emerald-600 shrink-0">+{money(r.total)}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          <button onClick={() => setModal(null)} className="mt-6 w-full bg-primary hover:bg-primary-hover text-white font-semibold rounded-md py-2.5 transition-colors">Cerrar</button>
        </Center>
      )}
    </div>
  );
}

function durationLabel(created_at, expires_at) {
  if (!created_at || !expires_at) return "-";
  const days = Math.round((new Date(expires_at) - new Date(created_at)) / 86400000);
  if (days <= 0) return "-";
  if (days >= 25) { const m = Math.round(days / 30) || 1; return `${m} ${m === 1 ? "mes" : "meses"}`; }
  return `${days} día${days === 1 ? "" : "s"}`;
}

function capitalizeFirst(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

function copyText(text) {
  navigator.clipboard?.writeText(text).then(() => toast.success("Copiado"));
}


function formatDeliveredForCopy(productName, d) {
  const p = parseDelivered(d);
  if (p.texto) return `${productName} | \n${p.texto}`;
  const lines = [`${productName} | `, `correo: ${p.correo}`, `contra: ${p.contrasena}`];
  if (p.url) lines.push(`url: ${p.url}`);
  if (p.perfil) {
    lines.push(`perfil: ${p.perfil}`);
    const pin = profilePin(productName, p.perfil);
    if (pin) lines.push(`PIN: ${pin}`);
  }
  if (p.dispositivo) lines.push(`dispositivo: ${p.dispositivo}`);
  return lines.join("\n");
}

function HistoryModal({ purchases, onClose, onReload }) {
  const [q, setQ] = useState("");

  const canvaItems = useMemo(() => {
    const list = [];
    purchases.forEach((s) => {
      (s.items || []).forEach((it) => {
        if (it.delivery_mode === "canva_invite") {
          list.push({ saleId: s.id, productId: it.product_id, name: it.product_name, total: it.quantity, delivered: it.delivered || [] });
        }
      });
    });
    return list;
  }, [purchases]);

  const stats = useMemo(() => {
    let totalCompras = 0, totalGastado = 0;
    const servicios = new Set();
    purchases.forEach((s) => {
      totalGastado += s.total;
      (s.items || []).forEach((it) => { totalCompras += it.quantity; servicios.add(it.product_name); });
    });
    return { totalCompras, totalGastado, promedio: totalCompras > 0 ? totalGastado / totalCompras : 0, servicios: servicios.size };
  }, [purchases]);

  const cards = useMemo(() => {
    const list = [];
    purchases.forEach((s) => {
      (s.items || []).forEach((it) => {
        if (it.delivery_mode === "canva_invite") return;
        (it.delivered && it.delivered.length ? it.delivered : [""]).forEach((d, di) => {
          const p = d ? parseDelivered(d) : {};
          list.push({
            key: `${s.id}-${it.product_id}-${di}`,
            name: it.product_name,
            usuario: p.texto || p.correo || d, contrasena: p.contrasena || "",
            url: p.url || "", perfil: p.perfil || "", dispositivo: p.dispositivo || "",
            pin: p.perfil ? profilePin(it.product_name, p.perfil) : null,
            fecha: s.created_at, precio: it.price,
            duracion: durationLabel(s.created_at, it.expires_at),
          });
        });
      });
    });
    return list;
  }, [purchases]);

  const filtered = cards.filter((c) => {
    if (!q) return true;
    const t = q.toLowerCase();
    return c.name.toLowerCase().includes(t) || c.usuario.toLowerCase().includes(t) || c.fecha.toLowerCase().includes(t);
  });

  const stat = [
    { n: stats.totalCompras, l: "Total Compras", c: "text-primary" },
    { n: money(stats.totalGastado), l: "Total Gastado", c: "text-primary" },
    { n: money(stats.promedio), l: "Promedio", c: "text-primary" },
    { n: stats.servicios, l: "Servicios", c: "text-emerald-500" },
  ];

  return (
    <div role="dialog" aria-modal="true" aria-label="Registro de Compras" className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden animate-fade-up shadow-md">
        <div className="flex items-center justify-between px-6 py-4 bg-primary text-white shrink-0">
          <div className="flex items-center gap-2"><FolderClock size={18} /><h3 className="font-head text-lg font-extrabold">Registro de Compras</h3></div>
          <button onClick={onClose} aria-label="Cerrar" className="text-white/80 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-6 overflow-y-auto">
          {canvaItems.length > 0 && (
            <div className="space-y-3 mb-5">
              {canvaItems.map((c) => (
                <div key={`${c.saleId}-${c.productId}`}>
                  {c.delivered.length > 0 && (
                    <p className="text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2 mb-1.5">
                      <b>{c.name}</b> -- invitado{c.delivered.length === 1 ? "" : "s"}: {c.delivered.join(", ")}
                    </p>
                  )}
                  {c.delivered.length < c.total && (
                    <CanvaInviteBox saleId={c.saleId} productId={c.productId} productName={c.name} total={c.total} sent={c.delivered.length} onSent={onReload} />
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {stat.map((s, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-4 text-center">
                <p className={`font-head text-xl font-extrabold ${s.c}`}>{s.n}</p>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mt-1">{s.l}</p>
              </div>
            ))}
          </div>
          <div className="relative mb-4">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por servicio, cuenta o fecha..."
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors" />
          </div>
          {filtered.length === 0 ? (
            <p className="text-slate-500 text-center py-16">Aún no has comprado ninguna cuenta.</p>
          ) : (
            <div className="space-y-3">
              {filtered.map((c) => (
                <div key={c.key} className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 pb-2 mb-2 border-b border-slate-200">
                    <Monitor size={15} className="text-primary" />
                    <h4 className="font-head font-bold text-slate-900 text-sm">{c.name}</h4>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <HRow icon={User} label="Usuario" value={c.usuario} copy />
                    <HRow icon={KeyRound} label="Contraseña" value={c.contrasena} copy />
                    {c.url && <HRow icon={Globe} label="URL" value={c.url} copy />}
                    {c.perfil && <HRow icon={User} label="Perfil" value={c.perfil} />}
                    {c.perfil && c.pin && <HRow icon={KeyRound} label="PIN" value={c.pin} />}
                    {c.dispositivo && <HRow icon={Monitor} label="Dispositivo" value={c.dispositivo} />}
                    <HRow icon={Calendar} label="Fecha" value={new Date(c.fecha).toLocaleString("es-MX")} />
                    <HRow icon={Clock} label="Duración" value={c.duracion} />
                    <HRow icon={DollarSign} label="Precio" value={money(c.precio)} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CanvaMailsModal({ onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/my/canva-invites").then(({ data }) => setItems(data)).catch(() => toast.error("No se pudo cargar tus invitaciones"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div role="dialog" aria-modal="true" aria-label="Mis invitaciones" className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full max-h-[85vh] flex flex-col overflow-hidden animate-fade-up shadow-md">
        <div className="flex items-center justify-between px-6 py-4 bg-primary text-white shrink-0">
          <div className="flex items-center gap-2"><Mail size={18} /><h3 className="font-head text-lg font-extrabold">Mis invitaciones</h3></div>
          <button onClick={onClose} aria-label="Cerrar" className="text-white/80 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-6 overflow-y-auto">
          {loading ? (
            <p className="text-slate-500 text-center py-10">Cargando...</p>
          ) : items.length === 0 ? (
            <p className="text-slate-500 text-center py-16">Aún no has pedido ninguna invitación.</p>
          ) : (
            <div className="space-y-3">
              {items.map((it) => {
                const invitado = it.status === "invitado";
                return (
                  <div key={it.id} className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p className="font-bold text-slate-900 text-sm truncate">{it.product_name}</p>
                      <span className={`shrink-0 text-[11px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 ${invitado ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                        {invitado ? "Invitación enviada" : "En proceso"}
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 break-all">{it.email}</p>
                    <p className="text-[11px] text-slate-400 mt-1">
                      {invitado
                        ? `Enviada el ${new Date(it.invited_at).toLocaleString("es-MX")}`
                        : `Pedida el ${new Date(it.created_at).toLocaleString("es-MX")}`}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function HRow({ icon: Icon, label, value, copy }) {
  return (
    <div>
      <span className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-slate-500"><Icon size={11} /> {label}</span>
      <div className="flex items-center gap-1.5 mt-0.5">
        <span className="font-semibold text-slate-800 break-all">{value || "-"}</span>
        {copy && value && (
          <button onClick={() => copyText(value)} aria-label={`Copiar ${label}`} className="w-5 h-5 shrink-0 grid place-items-center rounded bg-primary hover:bg-primary-hover text-white transition-colors"><Copy size={11} /></button>
        )}
      </div>
    </div>
  );
}

function CanvaInviteBox({ saleId, productId, productName, total, sent, onSent }) {
  const [email, setEmail] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const remaining = total - sent;
  const askConfirm = () => {
    if (!email.trim()) return toast.error("Escribe el correo para la invitación");
    setConfirming(true);
  };
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/my/purchases/${saleId}/canva-invite`, { product_id: productId, email: email.trim() });
      toast.success("Listo -- un administrador va a revisar tu solicitud y mandarte la invitación en breve.");
      setEmail(""); setConfirming(false);
      await onSent();
    } catch (ex) { toast.error(apiError(ex.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-emerald-200 rounded-lg px-4 py-3">
      <p className="font-bold text-slate-900 text-sm mb-1">{productName}</p>
      {confirming ? (
        <>
          <p className="text-xs text-slate-600 mb-2">¿Este correo es correcto? Ahí te va a llegar la invitación:</p>
          <p className="font-mono text-sm bg-slate-50 border border-slate-200 rounded-md px-3 py-2 mb-2 break-all">{email}</p>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setConfirming(false)} className="flex-1 border border-slate-300 hover:bg-slate-50 text-slate-600 font-semibold rounded-md py-2 text-sm transition-colors">Corregir</button>
            <button onClick={submit} disabled={busy} className="flex-1 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-semibold rounded-md py-2 text-sm transition-colors">{busy ? "..." : "Sí, es correcto"}</button>
          </div>
        </>
      ) : (
        <>
          <p className="text-xs text-slate-500 mb-2 flex items-center gap-1.5">
            <Mail size={12} className="text-emerald-500 shrink-0" />
            Da el correo al que quieres la invitación. Un administrador recibe tu solicitud y te agrega a mano ({sent}/{total} enviadas{remaining > 0 ? `, faltan ${remaining}` : ""}).
          </p>
          <div className="flex items-center gap-2">
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="tu@correo.com"
              className="flex-1 border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors" />
            <button onClick={askConfirm} className="shrink-0 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-md px-4 py-2 text-sm transition-colors">
              Invitar
            </button>
          </div>
        </>
      )}
    </div>
  );
}
function Pill({ icon: Icon, text, tone }) {
  const c = tone === "red" ? "text-red-600 border-slate-200" : tone === "green" ? "text-emerald-500 border-emerald-100" : "text-slate-600 border-slate-200";
  return <span className={`inline-flex items-center gap-1.5 bg-white border rounded-lg px-3 py-1.5 text-sm font-semibold ${c}`}><Icon size={14} /> {text}</span>;
}
function FloatBtn({ children, onClick, className = "", testid, label }) {
  return <button data-testid={testid} aria-label={label} onClick={onClick} className={`w-11 h-11 grid place-items-center rounded-full shadow-md transition-colors text-white ${className}`}>{children}</button>;
}
function Row({ label, value }) {
  return <div className="flex justify-between border-b border-slate-100 pb-2"><span className="text-slate-500">{label}</span><span className="font-semibold text-slate-800">{value}</span></div>;
}
function Center({ children, onClose }) {
  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="relative bg-white rounded-2xl max-w-md w-full max-h-[85vh] overflow-y-auto p-6 pr-12 animate-fade-up shadow-md">
        <button onClick={onClose} aria-label="Cerrar" className="absolute top-3 right-3 w-8 h-8 grid place-items-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 z-10"><X size={16} /></button>
        {children}
      </div>
    </div>
  );
}
